<template>
  <div ref="host" class="text-portrait">
    <img class="text-portrait__photo" :class="{ 'is-covered': ready }" :src="src" :alt="alt">
    <canvas
      ref="canvas"
      class="text-portrait__canvas"
      :class="{ 'is-ready': ready }"
      aria-hidden="true"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
      @touchstart.passive="burstGlitch"
    />
  </div>
</template>

<script>
/**
 * Renders an image as a dense typographic portrait (à la "The American President"
 * speech poster): the source text flows row by row and every glyph's shade and
 * font weight are driven by the luminance of the photo underneath it.
 * Hovering triggers an RGB (red/cyan) split glitch with horizontal slice tearing;
 * the same burst also fires on its own every `autoGlitchEvery` ms so the portrait
 * still shows off on touch devices and to visitors who never hover it.
 * The plain <img> stays underneath as the no-JS / failure fallback.
 *
 * Theme-aware: in dark mode it's luminous text on black (the poster look); in
 * light mode it flips to translucent ink on transparent canvas so the page's
 * newsprint texture shows through, and the glitch becomes a misregistered
 * print-plate effect instead of an additive RGB split.
 */
import { getCurrentTheme } from "@/helpers/theme";

const FONT_STACK = "\"Arial Narrow\", \"Helvetica Neue\", Helvetica, Arial, sans-serif";
const INK = { r: 26, g: 22, b: 18 };

export default {
  name: "text-portrait-glitch",
  props: {
    src: { type: String, required: true },
    text: { type: String, required: true },
    alt: { type: String, default: "" },
    // number of text rows the portrait is built from (more rows = finer detail)
    rows: { type: Number, default: 104 },
    // fraction of the frame kept clear above the top of the head
    headroom: { type: Number, default: 0.045 },
    // how often the portrait glitches by itself (ms); 0 disables it
    autoGlitchEvery: { type: Number, default: 10000 },
    // how long each unattended burst lasts (ms)
    autoGlitchDuration: { type: Number, default: 900 }
  },
  data () {
    return {
      ready: false
    };
  },
  created () {
    // non-reactive internals (canvases, timers, animation state)
    this.img = null;
    this.base = null;
    this.redLayer = null;
    this.cyanLayer = null;
    this.rafId = null;
    this.glitching = false;
    this.glitchState = null;
    this.burstTimer = null;
    this.autoTimer = null;
    this.hovering = false;
    this.visible = true;
    this.visibilityObserver = null;
    this.resizeTimer = null;
    this.resizeObserver = null;
    this.lastWidth = 0;
    this.reducedMotion = false;
    this.theme = "dark";
    this.themeObserver = null;
  },
  mounted () {
    this.reducedMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    this.theme = getCurrentTheme();
    // rebuild when the site scheme flips (nav toggle stamps <html data-theme>)
    this.themeObserver = new MutationObserver(() => {
      const theme = getCurrentTheme();
      if (theme !== this.theme) {
        this.theme = theme;
        if (this.img) this.build();
      }
    });
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
    const img = new Image();
    img.onload = () => {
      this.img = img;
      this.build();
      this.observeResize();
    };
    img.src = this.src;
  },
  beforeDestroy () {
    this.glitching = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    clearTimeout(this.burstTimer);
    clearTimeout(this.resizeTimer);
    clearInterval(this.autoTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.themeObserver) this.themeObserver.disconnect();
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    observeResize () {
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.$refs.host);
      } else {
        window.addEventListener("resize", this.onResize);
      }
    },
    onResize () {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const host = this.$refs.host;
        if (!host) return;
        const width = Math.round(host.getBoundingClientRect().width);
        if (width && width !== this.lastWidth) this.build();
      }, 180);
    },
    // deterministic pseudo-random generator so rebuilds look identical
    prng (seed) {
      let s = seed % 2147483647;
      if (s <= 0) s += 2147483646;
      return () => {
        s = (s * 16807) % 2147483647;
        return (s - 1) / 2147483646;
      };
    },
    build () {
      const host = this.$refs.host;
      const canvas = this.$refs.canvas;
      if (!host || !canvas || !this.img || !this.img.naturalWidth) return;

      const width = Math.round(host.getBoundingClientRect().width);
      if (!width) return;
      this.lastWidth = width;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = Math.round(width * dpr);
      const H = Math.round(W * (this.img.naturalHeight / this.img.naturalWidth));
      canvas.width = W;
      canvas.height = H;

      try {
        this.renderBase(W, H);
      } catch (e) {
        // e.g. tainted canvas if src ever becomes cross-origin — keep the plain photo
        this.ready = false;
        return;
      }
      this.makeChannelLayers(W, H);
      this.drawStill();
      this.ready = true;
      this.startAutoGlitch();
      this.observeVisibility();
    },
    renderBase (W, H) {
      // luminance map sampled from a downscaled copy of the photo
      const sw = 170;
      const sh = Math.max(1, Math.round(sw * H / W));
      const sample = document.createElement("canvas");
      sample.width = sw;
      sample.height = sh;
      const sctx = sample.getContext("2d");
      sctx.drawImage(this.img, 0, 0, sw, sh);
      const pixels = sctx.getImageData(0, 0, sw, sh).data;

      const count = sw * sh;
      const lum = new Float32Array(count);
      for (let i = 0; i < count; i++) {
        const p = i * 4;
        lum[i] = (0.2126 * pixels[p] + 0.7152 * pixels[p + 1] + 0.0722 * pixels[p + 2]) / 255;
      }

      // The photo is a light-background studio shot: flood-fill near-white cells
      // from the borders so the backdrop goes dark (like the poster) while bright
      // spots inside the face are kept.
      const BG_LUM = 0.86;
      const isBg = new Uint8Array(count);
      const stack = [];
      const push = (ix) => {
        if (ix >= 0 && ix < count && !isBg[ix] && lum[ix] > BG_LUM) {
          isBg[ix] = 1;
          stack.push(ix);
        }
      };
      for (let x = 0; x < sw; x++) {
        push(x);
        push((sh - 1) * sw + x);
      }
      for (let y = 0; y < sh; y++) {
        push(y * sw);
        push(y * sw + sw - 1);
      }
      while (stack.length) {
        const ix = stack.pop();
        const x = ix % sw;
        if (x > 0) push(ix - 1);
        if (x < sw - 1) push(ix + 1);
        push(ix - sw);
        push(ix + sw);
      }

      // stretch the subject's tonal range so the face uses the full scale
      const percentiles = (accept) => {
        const vals = [];
        for (let i = 0; i < count; i++) {
          if (accept(i)) vals.push(lum[i]);
        }
        vals.sort((a, b) => a - b);
        const lo = vals.length ? vals[Math.floor(vals.length * 0.05)] : 0;
        const hi = vals.length ? vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.95))] : 1;
        return { lo, hi, range: Math.max(0.001, hi - lo) };
      };
      const full = percentiles((i) => !isBg[i]);

      // face-weighted auto-crop: frame the well-lit part of the subject (the
      // face) like the reference poster instead of drawing lots of empty backdrop.
      // Percentile-trimmed bounds so a handful of stray bright cells at the
      // backdrop edge can't blow the box up to the whole frame.
      const xs = [];
      const ys = [];
      const subjectYs = [];
      for (let i = 0; i < count; i++) {
        if (isBg[i]) continue;
        const ny = Math.floor(i / sw) / (sh - 1);
        subjectYs.push(ny);
        if ((lum[i] - full.lo) / full.range < 0.35) continue;
        xs.push((i % sw) / (sw - 1));
        ys.push(ny);
      }
      xs.sort((a, b) => a - b);
      ys.sort((a, b) => a - b);
      subjectYs.sort((a, b) => a - b);
      const trim = (arr, f) => arr[Math.min(arr.length - 1, Math.floor(arr.length * f))];
      // the crop is a relative square so the canvas aspect is preserved
      let cropX = 0;
      let cropY = 0;
      let cropSize = 1;
      if (xs.length > 40) {
        const fx0 = trim(xs, 0.05);
        const fx1 = trim(xs, 0.95);
        const fy1 = trim(ys, 0.95);
        // true tip of the head: topmost grid row holding a few subject cells
        // (hair is darker than the bright "face" cells, so the face bbox
        // alone would behead the portrait)
        let subjectTop = 1;
        let runVal = -1;
        let runCount = 0;
        for (let k = 0; k < subjectYs.length; k++) {
          if (subjectYs[k] === runVal) {
            runCount++;
          } else {
            runVal = subjectYs[k];
            runCount = 1;
          }
          if (runCount >= 3) {
            subjectTop = runVal;
            break;
          }
        }
        // top-anchored framing like a sensible portrait: headroom above the
        // hair, face/chest filling the rest of the frame
        const heightNeed = (Math.min(1, fy1 + 0.06) - subjectTop) / (1 - this.headroom);
        cropSize = Math.min(1, Math.max(0.4, Math.max((fx1 - fx0) * 1.18, heightNeed)));
        cropY = Math.max(0, subjectTop - this.headroom * cropSize);
        if (cropY + cropSize > 1) cropY = 1 - cropSize;
        cropX = Math.min(1 - cropSize, Math.max(0, (fx0 + fx1) / 2 - cropSize / 2));
      }

      const cellAt = (nx, ny) => {
        const px = Math.min(sw - 1, Math.max(0, Math.round(nx * (sw - 1))));
        const py = Math.min(sh - 1, Math.max(0, Math.round(ny * (sh - 1))));
        return py * sw + px;
      };
      // contrast anchored to the face's own tonal band (not the near-black
      // clothing) so eyes, glasses and shadows stay carved into the bright skin
      const isFaceCell = (i) => !isBg[i] && (lum[i] - full.lo) / full.range >= 0.35;
      const faceVals = [];
      for (let i = 0; i < count; i++) {
        if (isFaceCell(i)) faceVals.push(lum[i]);
      }
      faceVals.sort((a, b) => a - b);
      const faceLo = faceVals.length ? faceVals[Math.floor(faceVals.length * 0.1)] : full.lo;
      const faceHi = faceVals.length ? faceVals[Math.min(faceVals.length - 1, Math.floor(faceVals.length * 0.9))] : full.hi;
      const faceRange = Math.max(0.02, faceHi - faceLo);

      const shadeAt = (nx, ny) => {
        const i = cellAt(cropX + nx * cropSize, cropY + ny * cropSize);
        if (isBg[i]) return { v: 0, bg: true };
        const stretched = Math.min(1, Math.max(0, (lum[i] - faceLo) / faceRange));
        return { v: Math.pow(stretched, 1.15), bg: false };
      };

      const light = this.theme === "light";
      const base = document.createElement("canvas");
      base.width = W;
      base.height = H;
      const ctx = base.getContext("2d");
      if (!light) {
        // dark: the poster's black plate; light: transparent so the page's
        // newsprint texture shows through between the ink
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, W, H);
      }
      ctx.textBaseline = "middle";

      const fontSize = Math.max(8, H / this.rows);
      const lineHeight = fontSize * 1.04;
      const letterSpace = fontSize * 0.07;
      const wordGap = fontSize * 0.45;
      const font = (weight, italic) => `${italic ? "italic " : ""}${weight} ${fontSize}px ${FONT_STACK}`;
      const words = this.text.toUpperCase().split(/\s+/).filter(Boolean);
      if (!words.length) words.push("KOBINA");
      const rand = this.prng(words.length * 7919 + W);

      let wordIndex = 0;
      const totalRows = Math.ceil(H / lineHeight);
      for (let row = 0; row < totalRows; row++) {
        const cy = row * lineHeight + lineHeight / 2;
        let x = 0;
        let guard = 0;
        while (x < W && guard++ < 400) {
          const word = words[wordIndex % words.length];
          wordIndex++;
          const italic = rand() < 0.12;
          ctx.font = font(700, italic);
          const wordWidth = ctx.measureText(word).width + (word.length - 1) * letterSpace;
          if (x > 0 && x + wordWidth > W) break;
          for (let g = 0; g < word.length; g++) {
            const ch = word[g];
            ctx.font = font(700, italic);
            const approx = ctx.measureText(ch).width;
            const cell = shadeAt((x + approx / 2) / W, cy / H);
            // backdrop cells stay a faint whisper of text; every subject cell
            // sits clearly above that floor so even near-black hair and
            // clothing read as a silhouette, and the face gets the full
            // tonal + weight ramp on top
            let weight;
            if (light) {
              // printed page: ink density is the inverse of luminance —
              // hair/shadows press dark bold ink, highlights stay faint
              const ink = cell.bg ? 0.07 : Math.min(1, 0.18 + 0.82 * (1 - cell.v));
              weight = ink > 0.72 ? 800 : ink > 0.5 ? 700 : ink > 0.3 ? 600 : 400;
              ctx.fillStyle = `rgba(${INK.r},${INK.g},${INK.b},${ink.toFixed(3)})`;
            } else {
              let shade;
              if (cell.bg) {
                shade = 20;
                weight = 400;
              } else {
                shade = Math.round(255 * Math.min(1, 0.24 + cell.v * 0.76));
                weight = cell.v > 0.6 ? 800 : cell.v > 0.36 ? 700 : cell.v > 0.16 ? 600 : 500;
              }
              ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
            }
            ctx.font = font(weight, italic);
            ctx.fillText(ch, x, cy);
            x += ctx.measureText(ch).width + letterSpace;
          }
          x += wordGap;
        }
      }
      this.base = base;
    },
    makeChannelLayers (W, H) {
      const light = this.theme === "light";
      const tint = (color) => {
        const layer = document.createElement("canvas");
        layer.width = W;
        layer.height = H;
        const ctx = layer.getContext("2d");
        ctx.drawImage(this.base, 0, 0);
        // dark: multiply isolates a channel of the luminous text;
        // light: the base is transparent ink, so recolour the ink itself
        ctx.globalCompositeOperation = light ? "source-in" : "multiply";
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, W, H);
        return layer;
      };
      // dark: pure red + cyan recombine to the original grey when aligned;
      // light: red/cyan print plates that slip out from under the black one
      this.redLayer = tint(light ? "#e4001c" : "#ff0000");
      this.cyanLayer = tint(light ? "#0090c8" : "#00ffff");
    },
    drawStill () {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.base) return;
      const ctx = canvas.getContext("2d");
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      if (this.theme === "light") {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(this.base, 0, 0);
    },
    drawStaticSplit () {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const ctx = canvas.getContext("2d");
      ctx.globalCompositeOperation = "source-over";
      if (this.theme === "light") {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = 0.85;
        ctx.drawImage(this.cyanLayer, -2 * dpr, 0);
        ctx.drawImage(this.redLayer, 2 * dpr, 0);
        ctx.globalAlpha = 1;
        ctx.drawImage(this.base, 0, 0);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "lighter";
        ctx.drawImage(this.cyanLayer, -2 * dpr, 0);
        ctx.drawImage(this.redLayer, 2 * dpr, 0);
        ctx.globalCompositeOperation = "source-over";
      }
    },
    startGlitch () {
      if (!this.ready || this.glitching) return;
      this.glitching = true;
      if (this.reducedMotion) {
        // motion-sensitive users get a subtle static split instead of animation
        this.drawStaticSplit();
        return;
      }
      this.glitchState = { nextShuffle: 0, dx: 0, dyR: 0, dyC: 0, slices: [] };
      const loop = (t) => {
        if (!this.glitching) return;
        this.renderGlitchFrame(t);
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    },
    stopGlitch () {
      if (!this.glitching) return;
      this.glitching = false;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.ready) this.drawStill();
    },
    burstGlitch (duration) {
      // touch devices don't hover — give them a short glitch burst instead
      this.startGlitch();
      clearTimeout(this.burstTimer);
      this.burstTimer = setTimeout(() => {
        // a pointer that arrived mid-burst owns the effect now; let it hold
        if (!this.hovering) this.stopGlitch();
      }, typeof duration === "number" ? duration : 700);
    },
    onEnter () {
      this.hovering = true;
      clearTimeout(this.burstTimer);
      this.startGlitch();
    },
    onLeave () {
      this.hovering = false;
      this.stopGlitch();
    },
    startAutoGlitch () {
      // fires on its own so the portrait still performs without a pointer
      if (this.autoTimer || this.reducedMotion || this.autoGlitchEvery <= 0) return;
      this.autoTimer = setInterval(() => {
        if (!this.ready || this.hovering || this.glitching) return;
        // don't burn frames on a portrait nobody can see
        if (!this.visible || document.hidden) return;
        this.burstGlitch(this.autoGlitchDuration);
      }, this.autoGlitchEvery);
    },
    observeVisibility () {
      if (this.visibilityObserver || typeof IntersectionObserver === "undefined") return;
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
      }, { threshold: 0.15 });
      this.visibilityObserver.observe(this.$refs.host);
    },
    renderGlitchFrame (t) {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.base) return;
      const st = this.glitchState;
      const W = canvas.width;
      const H = canvas.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      // re-roll offsets every few frames; occasional spikes tear harder
      if (t >= st.nextShuffle) {
        const spike = Math.random() < 0.16;
        st.dx = (spike ? 8 + Math.random() * 10 : 1.5 + Math.random() * 4) * dpr;
        st.dyR = (Math.random() - 0.5) * 2 * dpr;
        st.dyC = (Math.random() - 0.5) * 2 * dpr;
        const sliceCount = spike ? 3 + Math.floor(Math.random() * 4) : Math.floor(Math.random() * 3);
        st.slices = [];
        for (let i = 0; i < sliceCount; i++) {
          st.slices.push({
            y: Math.random() * H,
            h: (2 + Math.random() * 26) * dpr,
            dx: (Math.random() - 0.5) * (spike ? 60 : 26) * dpr
          });
        }
        st.nextShuffle = t + 50 + Math.random() * 110;
      }

      const ctx = canvas.getContext("2d");
      ctx.globalCompositeOperation = "source-over";
      if (this.theme === "light") {
        // misregistered print plates: translucent red/cyan inks slipping
        // out from under the black plate
        ctx.clearRect(0, 0, W, H);
        ctx.globalAlpha = 0.85;
        ctx.drawImage(this.cyanLayer, -st.dx, st.dyC);
        ctx.drawImage(this.redLayer, st.dx, st.dyR);
        ctx.globalAlpha = 1;
        ctx.drawImage(this.base, st.dx * 0.25, 0);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = "lighter";
        ctx.drawImage(this.cyanLayer, -st.dx, st.dyC);
        ctx.drawImage(this.redLayer, st.dx, st.dyR);
        ctx.globalCompositeOperation = "source-over";
      }
      for (let i = 0; i < st.slices.length; i++) {
        const s = st.slices[i];
        const h = Math.min(s.h, H - s.y);
        if (h <= 0) continue;
        // the light base is transparent ink, so displace (erase then draw)
        // rather than stamp another copy on top
        if (this.theme === "light") ctx.clearRect(0, s.y, W, h);
        ctx.drawImage(this.base, 0, s.y, W, h, s.dx, s.y, W, h);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.text-portrait {
  position: relative;
  width: 100%;

  &__photo {
    display: block;
    width: 100%;

    // once the canvas has rendered it fully replaces the photo; the canvas
    // is transparent in light mode, so the photo must actually vanish
    // (visibility keeps the layout box)
    &.is-covered {
      visibility: hidden;
    }
  }

  &__canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    opacity: 0;
    transition: opacity 0.8s ease;
    cursor: crosshair;

    &.is-ready {
      opacity: 1;
    }
  }
}
</style>
